import { useState, useEffect, useContext, useRef } from "react";
import {
  Search, MessageSquare, ArrowLeft, Send,
  User, Paperclip, X, CheckCircle2, Clock, ListChecks
} from "lucide-react";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import SupStatusBadge from "../../../components/general/SupportSharedFiles/SupStatusBadge";
import SupEmptyState from "../../../components/general/SupportSharedFiles/SupEmptyState";
import SupLoader from "../../../components/general/SupportSharedFiles/SupLoader";
import "../../../assets/css/general/Support/supshare.css";
import "../../../assets/css/vendors/HotelScreens/vndsupporttickets.css";
import VendorLayout from "../../../layout/HotelVendorLayout/VendorLayout";


const FILTERS = ["all", "open", "in_progress", "resolved"];
 
const VndSupportTickets = () => {
  const { baseUrl, authToken, baseUrlForFile } = useContext(AppContext);
  const bottomRef = useRef(null);
 
  
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");
 
 
  const [activeChatId, setActiveChatId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
 
  
  const fetchTickets = () => {
    setListLoading(true);
    axios
      .get(`${baseUrl}/vendor/support/tickets`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params: { status: filter },
      })
      .then((res) => {
        setTickets(res.data.supporttickets || []);
        setListError("");
      })
      .catch((err) => {
        console.error(err.response?.data || err.message);
        setListError("Failed to load support tickets.");
      })
      .finally(() => setListLoading(false));
  };
 
  
  const openChat = (ticket) => {
    setActiveChatId(ticket.id);
    setChatLoading(true);
 
    axios
      .get(`${baseUrl}/vendor/support/tickets/${ticket.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        const t = res.data.ticket;
        setDetail(t);
        setMessages(t?.jjconversations?.[0]?.jjchats || []);
      })
      .catch((err) => {
        console.error(err.response?.data || err.message);
      })
      .finally(() => setChatLoading(false));
  };
 

 const sendReply = () => {
  if (!reply.trim()) return;

  if (detail?.status === "resolved") {
    alert("Ticket is resolved. You cannot reply.");
    return;
  }

  setSending(true);

  const formData = new FormData();
  formData.append("message", reply);

  axios
    .post(`${baseUrl}/vendor/support/tickets/${activeChatId}/messages`, formData, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "multipart/form-data",
      },
    })
    .then((res) => {
      setMessages((prev) => [...prev, res.data.chat]);
      setReply("");
    })
    .finally(() => setSending(false));
};
  
  const updateStatus = (status) => {
    axios
      .put(`${baseUrl}/vendor/support/tickets/${activeChatId}/status`, { status }, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        setDetail((prev) => ({ ...prev, status }));
        setTickets((prev) =>
          prev.map((t) => (t.id === activeChatId ? { ...t, status } : t))
        );
      })
      .catch((err) => {
        console.error(err.response?.data || err.message);
        alert("Failed to update status.");
      });
  };
 
  useEffect(() => { fetchTickets(); }, [filter, authToken]);
 
  
  useEffect(() => {
    if (!activeChatId) return;
 
    const pollMessages = () => {
      axios
        .get(`${baseUrl}/vendor/support/tickets/${activeChatId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        })
        .then((res) => {
          const chats = res.data.ticket?.jjconversations?.[0]?.jjchats || [];
          setMessages(chats);
        })
        .catch(() => {});
    };
 
    const interval = setInterval(pollMessages, 5000);
    return () => clearInterval(interval);
  }, [activeChatId, authToken]);
 
  useEffect(() => {
    if (activeChatId) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);
 
  const filtered = tickets.filter((t) => {
    const q = search.toLowerCase();
    return (
      t.category?.toLowerCase().includes(q) ||
      t.raisedby?.toLowerCase().includes(q) ||
      String(t.id).includes(q)
    );
  });
 
  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleTimeString("en-PK", { hour: "2-digit", minute: "2-digit" });
  };
 
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-PK", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };
 
  
  if (activeChatId) {
    const ticket = detail || tickets.find((t) => t.id === activeChatId);
 
    return (
      <VendorLayout>
      <div className="vnd-st-page">
        <div className="vnd-st-chat">
          {/* Chat Header */}
          <div className="vnd-st-chat__header">
            <div className="vnd-st-chat__header-left">
              <button className="vnd-st-back-btn"
                onClick={() => { setActiveChatId(null); setDetail(null); setMessages([]); }}>
                <ArrowLeft size={16} />
              </button>
              {ticket && (
                <div>
                  <p className="vnd-st-chat__title">{ticket.category}</p>
                  <div className="vnd-st-chat__meta">
                    <span className="vnd-st-chat__id">#{ticket.id}</span>
                    <span>·</span>
                    <span>By: {ticket.raisedby}</span>
                    {ticket && <SupStatusBadge status={ticket.status} />}
                  </div>
                </div>
              )}
            </div>
            {/* Status controls */}
            {ticket && ticket.status !== "resolved" && (
              <div className="vnd-st-status-btns">
                {ticket.status === "open" && (
                  <button className="vnd-st-status-btn vnd-st-status-btn--progress"
                    onClick={() => updateStatus("in_progress")}>
                    <Clock size={12} /> Mark In Progress
                  </button>
                )}
                <button className="vnd-st-status-btn vnd-st-status-btn--resolve"
                  onClick={() => updateStatus("resolved")}>
                  <CheckCircle2 size={12} /> Mark Resolved
                </button>
              </div>
            )}
            {ticket && ticket.status === "resolved" && (
              <div className="vnd-st-resolved-pill">
                <CheckCircle2 size={13} /> Resolved
              </div>
            )}
          </div>
 
          {/* Ticket + Booking Info Strip */}
          {detail?.jjbooking && (
            <div className="vnd-st-booking-strip">
              <span>Booking #{detail.jjbooking.id}</span>
              <span className="vnd-st-strip-sep">·</span>
              <span>{detail.jjbooking.booking_type}</span>
              <span className="vnd-st-strip-sep">·</span>
              <span>Customer: {detail.jjcustomer?.name || "—"}</span>
              <span className="vnd-st-strip-sep">·</span>
              <span>PKR {Number(detail.jjbooking.total_amount || 0).toLocaleString()}</span>
            </div>
          )}
 
          {/* Messages */}
          <div className="vnd-st-chat__messages">
            {chatLoading && <SupLoader label="Loading conversation..." />}
            {!chatLoading && messages.length === 0 && (
              <div className="vnd-st-chat__empty">
                No messages yet. Reply below to respond.
              </div>
            )}
            {!chatLoading && messages.map((msg, idx) => (
              <div
                key={msg.id || idx}
                className={`vnd-st-msg ${msg.sender === "vendor" ? "vnd-st-msg--self" : "vnd-st-msg--other"}`}
              >
                {msg.sender !== "vendor" && (
                  <div className={`vnd-st-msg__avatar vnd-st-msg__avatar--${msg.sender}`}>
                    <User size={13} />
                  </div>
                )}
                <div className="vnd-st-msg__bubble">
                  <p className="vnd-st-msg__sender">{msg.sender}</p>
                  {msg.message && <p className="vnd-st-msg__text">{msg.message}</p>}
                  {msg.file && (
                    <img
                      src={`${baseUrlForFile}/${msg.file}`}
                      alt="attachment"
                      className="vnd-st-msg__img"
                      onClick={() => setPreviewImage(`${baseUrlForFile}/${msg.file}`)}
                    />
                  )}
                  <span className="vnd-st-msg__time">{formatTime(msg.created_at)}</span>
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
 
       
          {(!ticket || ticket.status !== "resolved") && (
            <div className="vnd-st-chat__footer">
              <div className="vnd-st-chat__input-row">
                <input
                  className="vnd-st-reply-input"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendReply()}
                  placeholder="Type your reply..."
                />
                <button className="vnd-st-send-btn" onClick={sendReply}
                  disabled={sending || !reply.trim()}>
                  <Send size={14} />
                </button>
              </div>
            </div>
          )}
          {ticket && ticket.status === "resolved" && (
            <div className="vnd-st-resolved-footer">✓ This ticket is resolved</div>
          )}
        </div>
 
        {previewImage && (
          <div className="vnd-st-modal" onClick={() => setPreviewImage(null)}>
            <button className="vnd-st-modal__close" onClick={() => setPreviewImage(null)}><X size={14} /></button>
            <img src={previewImage} alt="preview" className="vnd-st-modal__img"
              onClick={(e) => e.stopPropagation()} />
          </div>
        )}
      </div>
      </VendorLayout>
    );
  }
  return (
    <VendorLayout>
    <div className="vnd-st-page">
      {/* Header */}
      <div className="vnd-st-header">
        <div>
          <h2 className="vnd-st-heading">Support Tickets</h2>
          <p className="vnd-st-subtext">Complaints and issues raised against your property</p>
        </div>
        <div className="vnd-st-search-wrap">
          <Search size={14} className="vnd-st-search-icon" />
          <input className="vnd-st-search" value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tickets..." />
        </div>
      </div>
 
      {listError && <div className="sup-error-banner">{listError}</div>}
 
      {/* Stats */}
      <div className="vnd-st-stats">
        {[
          { label: "Total", value: tickets.length, icon: ListChecks, cls: "--total" },
          { label: "Open",  value: tickets.filter((t) => t.status === "open").length, icon: Clock, cls: "--open" },
          { label: "Resolved", value: tickets.filter((t) => t.status === "resolved").length, icon: CheckCircle2, cls: "--resolved" },
        ].map((s) => (
          <div key={s.label} className="vnd-st-stat-card">
            <div className={`vnd-st-stat-icon vnd-st-stat-icon${s.cls}`}>
              <s.icon size={18} />
            </div>
            <div>
              <p className="vnd-st-stat-value">{s.value}</p>
              <p className="vnd-st-stat-label">{s.label}</p>
            </div>
          </div>
        ))}
      </div>
 
      {/* Filters */}
      <div className="vnd-st-filters">
        {FILTERS.map((f) => (
          <button key={f}
            className={`vnd-st-filter-btn ${filter === f ? "vnd-st-filter-btn--active" : ""}`}
            onClick={() => setFilter(f)}>
            {f.replace("_", " ")}
          </button>
        ))}
      </div>
 
      {/* List */}
      {listLoading ? (
        <SupLoader label="Loading tickets..." />
      ) : filtered.length === 0 ? (
        <SupEmptyState icon={MessageSquare} title="No tickets found"
          subtitle="No complaints raised against your property" />
      ) : (
        <div className="vnd-st-list">
          {filtered.map((ticket, i) => (
            <div key={ticket.id} className="vnd-st-ticket"
              style={{ animationDelay: `${i * 0.04}s` }}>
              <div className="vnd-st-ticket__left">
                <div className="vnd-st-ticket__top">
                  <span className="vnd-st-ticket__id">#{ticket.id}</span>
                  <SupStatusBadge status={ticket.status} />
                </div>
                <p className="vnd-st-ticket__title">{ticket.category}</p>
                <p className="vnd-st-ticket__desc">{ticket.description}</p>
                <div className="vnd-st-ticket__footer">
                  <span>Raised by: <strong>{ticket.raisedby}</strong></span>
                  <span className="vnd-st-sep">·</span>
                  <span>{formatDate(ticket.created_at)}</span>
                </div>
              </div>
              <button className="vnd-st-open-btn" onClick={() => openChat(ticket)}>
                <MessageSquare size={13} /> Reply
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
    </VendorLayout>
  );
};
 
export default VndSupportTickets;